import { create } from 'zustand';

// Canonical numeric role -> role NAME map. This is the single source of truth for the
// string role representation the frontend gates on (mirror of useAuthStore's name->number
// ROLE_MAP). The backend also sends the role as a name via /bootstrap; toRoleName() lets us
// accept either a name ("Owner") or a numeric value/string (5 / "5") and always store the
// canonical name — otherwise a numeric role would silently defeat the name-based
// SuperAdmin/Owner bypass below and lock those users out of every non-core module.
const ROLE_NAMES: Record<number, string> = {
  0: 'SuperAdmin',
  1: 'Admin',
  2: 'Accountant',
  3: 'Sales',
  4: 'POSOperator',
  5: 'Owner',
  6: 'HRManager',
};

export const toRoleName = (raw: string | number | null | undefined): string => {
  if (raw === null || raw === undefined || raw === '') return '';
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isNaN(num) && ROLE_NAMES[num]) return ROLE_NAMES[num];
  return String(raw); // already a name (or an unknown label) -> keep as-is
};

interface ModuleInfo {
  id: string;
  name: string;
  code: string;
  description: string;
  icon: string;
  sortOrder: number;
  isActive: boolean;
  isEnabled: boolean;
}

interface PermissionState {
  role: string;
  permissions: string[];
  enabledModules: string[];
  modules: ModuleInfo[];
  isLoaded: boolean;
  isLoading: boolean;

  setBootstrapData: (data: {
    role: string;
    permissions: string[];
    enabledModules: string[];
    modules: ModuleInfo[];
  }) => void;
  markDegraded: (role: string) => void;
  isModuleEnabled: (code: string) => boolean;
  reset: () => void;
}

export const usePermissionStore = create<PermissionState>()((set, get) => ({
  role: '',
  permissions: [],
  enabledModules: [],
  modules: [],
  isLoaded: false,
  isLoading: false,

  setBootstrapData: ({ role, permissions, enabledModules, modules }) => {
    set({
      // Normalize to a canonical role NAME so downstream name-based checks (isModuleEnabled,
      // observability) work whether bootstrap supplied "Owner" or a numeric role like 5 / "5".
      role: toRoleName(role),
      permissions: permissions || [],
      enabledModules: enabledModules || [],
      modules: modules || [],
      isLoaded: true,
      isLoading: false,
    });
  },

  // Degraded bootstrap (network/5xx/timeout — NOT a 401): entitlements are UNKNOWN, not empty.
  // Leave isLoaded=false so ModuleGuard resolves 'unavailable' (retryable) instead of 'locked'
  // (the "upgrade your plan" paywall). Otherwise a transient blip masquerades as a plan
  // restriction for every module the user actually owns.
  markDegraded: (role: string) => {
    set({
      role: toRoleName(role),
      permissions: [],
      enabledModules: [],
      modules: [],
      isLoaded: false,
      isLoading: false,
    });
  },

  isModuleEnabled: (code: string) => {
    const { role, enabledModules } = get();
    // Owner and SuperAdmin see everything in their management context
    if (role === 'SuperAdmin' || role === 'Owner') return true;
    return enabledModules.includes(code);
  },

  reset: () => {
    set({
      role: '',
      permissions: [],
      enabledModules: [],
      modules: [],
      isLoaded: false,
      isLoading: false,
    });
  },
}));
